import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeFlourish } from "./home/ThemeFlourish";
import { Skull, Cog, Flame } from "lucide-react";
import { cardImage, readStreak } from "../pages/homeConstants";

const DECK_SIZE = 5;
// One riffle cycle. Kept deliberately brisk — a slow ease reads as floating, not shuffling.
const RIFFLE_MS = 700;

// Per-card riffle choreography. The original animation slid every card out and straight
// back on one path (x: [0, ±96, 0]) with a FIXED stacking order, which reads as a wobble.
// A riffle needs four things this now does:
//   1. the deck SPLITS into two packets travelling opposite ways, and visibly HOLDS
//      apart so you actually see two halves (the phase the old version never had),
//   2. each packet FANS around its inner-bottom corner (see transformOrigin below)
//      rather than sliding flat — that pivot is what makes it look like cards, not tiles,
//   3. the halves snap back and INTERLEAVE on a staggered per-card delay, and
//   4. the stacking order actually CHANGES between cycles (see `cycle`) — without
//      reordering, no amount of motion looks like a shuffle.
// Derived from the card index so the deck is deterministic across renders while still
// avoiding two rigid, identical-looking groups.
function riffleKeyframes(i) {
  const dir = i % 2 === 0 ? -1 : 1;      // which packet this card belongs to
  const spread = 62 + (i % 3) * 9;       // how far the packet fans out
  const fan = dir * (18 + (i % 3) * 5);  // fan rotation about the corner pivot
  const peak = -30 - (i % 4) * 6;        // arc height as it passes nearest the viewer
  const hold = -12 - (i % 3) * 5;        // lift while held apart
  const rest = (i - 2) * 2.5;            // squared-up resting tilt
  return {
    //     stacked   split        hold         interleave            overshoot  settle
    x: [0, dir * spread, dir * spread, dir * spread * 0.18, dir * 3, 0],
    y: [0, hold, hold, peak, 4, 0],
    rotate: [rest, fan, fan, fan * 0.3, dir * -1.5, rest],
    scale: [1, 1.01, 1.01, 1.05, 0.985, 1],
    opacity: 1,
  };
}
// 6 keyframes -> 5 timing stops and 5 easings. Snap out, hold, snap in, compress, settle.
const RIFFLE_TIMES = [0, 0.22, 0.34, 0.66, 0.84, 1];
const RIFFLE_EASE = ["easeOut", "linear", "easeIn", "easeOut", "easeInOut"];

// Branded card back shown on every shuffling card (photo only appears on the landed winner)
function CardBack({ light, seasonItem, theme, golden }) {
  // 30-day streak reward: loyal dealers shuffle a gilded deck in every realm.
  if (golden) {
    const gold = "#E6B23A";
    return (
      <div
        className="absolute inset-0 grid place-items-center overflow-hidden"
        style={{ background: "linear-gradient(155deg, #2A1D06 0%, #43300C 46%, #241804 100%)" }}
        data-testid="card-back-golden"
      >
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(115deg, transparent 34%, rgba(255,224,138,0.26) 46%, rgba(255,246,214,0.5) 50%, rgba(255,224,138,0.26) 54%, transparent 66%)", animation: "ffGoldSheen 3.6s ease-in-out infinite" }} />
        <div className="absolute inset-2 rounded-xl border" style={{ borderColor: `${gold}CC`, boxShadow: "inset 0 0 16px rgba(230,178,58,0.4)" }} />
        <div className="absolute inset-[10px] rounded-lg border" style={{ borderColor: `${gold}55` }} />
        <div className="relative flex flex-col items-center gap-2">
          <Flame className="h-12 w-12" strokeWidth={1.4} style={{ color: "#F3D9A0", filter: "drop-shadow(0 0 10px rgba(230,178,58,0.85))" }} />
          <span className="font-serif text-[10px] uppercase tracking-[0.4em]" style={{ color: "#F3D9A0" }}>Fork · Fate</span>
          <span className="font-sans text-[8px] font-bold uppercase tracking-[0.3em]" style={{ color: gold }}>30-Day Devotee</span>
        </div>
      </div>
    );
  }
  if (seasonItem) {
    return (
      <div className="absolute inset-0 grid place-items-center bg-[#F5F0E6]" data-testid="card-back">
        <img src={seasonItem} alt="" className="h-3/4 w-3/4 object-contain drop-shadow-md" />
      </div>
    );
  }
  if (light) {
    return (
      <div className="absolute inset-0 bg-[#F5F0E6]" data-testid="card-back">
        <img src="/card-back-light.png" alt="" className="h-full w-full object-cover" />
      </div>
    );
  }
  if (theme === "cyber") {
    return (
      <div className="absolute inset-0 grid place-items-center overflow-hidden bg-[#080B18]" data-testid="card-back">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 46%, rgba(34,224,224,0.20), rgba(0,0,0,0) 66%)" }} />
        <div className="absolute inset-2 rounded-xl border border-[#22E0E0]/60" style={{ boxShadow: "inset 0 0 14px rgba(34,224,224,0.45)" }} />
        <img src="/cyber-spinner-suv.png" alt="" className="w-[84%] object-contain" style={{ filter: "drop-shadow(0 0 10px rgba(34,224,224,0.8)) drop-shadow(0 0 20px rgba(199,125,255,0.55))" }} />
        <span className="absolute bottom-4 font-sans text-[8px] font-bold uppercase tracking-[0.45em] text-[#FF6FCB]" style={{ textShadow: "0 0 8px rgba(255,47,176,0.7)" }}>Neon Nights</span>
      </div>
    );
  }
  if (theme === "tiki") {
    const accent = "#F0A24E";
    return (
      <div className="absolute inset-0 grid place-items-center overflow-hidden bg-[#141210]" data-testid="card-back">
        <div className="absolute inset-2 rounded-xl border" style={{ borderColor: `${accent}88` }} />
        <div className="absolute inset-[10px] rounded-lg border" style={{ borderColor: `${accent}33` }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(240,162,78,0.18), rgba(0,0,0,0) 62%)" }} />
        <img src="/tiki-guy-card.png" alt="" className="h-3/4 w-auto max-w-[78%] object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.65)]" />
      </div>
    );
  }
  if (theme === "steam") {
    const brass = "#D9A44E";
    return (
      <div className="absolute inset-0 grid place-items-center overflow-hidden" data-testid="card-back"
        style={{ background: "radial-gradient(circle at 50% 42%, #3A2A18 0%, #1A120B 72%)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 44%, rgba(217,164,78,0.22), rgba(0,0,0,0) 62%)" }} />
        <div className="relative flex items-center justify-center">
          <Cog className="h-24 w-24" strokeWidth={1.1} style={{ color: brass, filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))" }} />
          <Cog className="absolute -bottom-5 -right-6 h-12 w-12" strokeWidth={1.15} style={{ color: "#B9833A" }} />
          <span className="absolute h-3 w-3 rounded-full" style={{ background: "radial-gradient(circle at 35% 30%, #F3D28A, #8A5E24)" }} />
        </div>
        <div className="absolute inset-2 rounded-xl border" style={{ borderColor: `${brass}99`, boxShadow: "inset 0 0 12px rgba(217,164,78,0.32)" }} />
        <div className="absolute inset-[10px] rounded-lg border" style={{ borderColor: `${brass}40` }} />
        {[["7px", "7px", "auto", "auto"], ["7px", "auto", "auto", "7px"], ["auto", "7px", "7px", "auto"], ["auto", "auto", "7px", "7px"]].map((p) => (
          <span key={p.join("-")} className="absolute h-1.5 w-1.5 rounded-full" style={{ top: p[0], right: p[1], bottom: p[2], left: p[3], background: "radial-gradient(circle at 35% 30%, #F3D28A, #7A5220)" }} />
        ))}
        <span className="absolute bottom-4 font-serif text-[9px] uppercase tracking-[0.4em]" style={{ color: brass }}>Clockwork</span>
      </div>
    );
  }
  if (theme === "fantasy") {
    const gold = "#E6B23A";
    return (
      <div className="absolute inset-0 grid place-items-center overflow-hidden bg-[#0E0906]" data-testid="card-back">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 44%, rgba(230,178,58,0.22), rgba(0,0,0,0) 62%)" }} />
        <img src="/logo-ouroboros.png" alt="" className="h-3/4 w-auto max-w-[82%] object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.7)]" />
        <div className="absolute inset-2 rounded-xl border" style={{ borderColor: `${gold}99`, boxShadow: "inset 0 0 12px rgba(230,178,58,0.3)" }} />
        <div className="absolute inset-[10px] rounded-lg border" style={{ borderColor: `${gold}40` }} />
        <span className="absolute bottom-4 font-serif text-[9px] uppercase tracking-[0.4em]" style={{ color: gold }}>Fork · Fate</span>
      </div>
    );
  }
  if (theme === "fairy") {
    const green = "#5EE0A8";
    return (
      <div className="absolute inset-0 grid place-items-center overflow-hidden bg-[#0A1C11]" data-testid="card-back">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 44%, rgba(94,224,168,0.2), rgba(0,0,0,0) 62%)" }} />
        <img src="/fairy-mushroom.png" alt="" className="h-3/4 w-auto max-w-[78%] object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]" />
        <div className="absolute inset-2 rounded-xl border" style={{ borderColor: `${green}99`, boxShadow: "inset 0 0 12px rgba(94,224,168,0.3)" }} />
        <div className="absolute inset-[10px] rounded-lg border" style={{ borderColor: `${green}40` }} />
        <span className="absolute bottom-4 font-serif text-[9px] uppercase tracking-[0.4em]" style={{ color: green }}>Fairy Gully</span>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 bg-[#0E0E0E]" data-testid="card-back">
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 42%, rgba(224,30,38,0.20), rgba(0,0,0,0) 62%)" }}
      />
      <div className="absolute inset-2 rounded-xl border border-[#E01E26]/70" />
      <div className="absolute inset-[10px] rounded-lg border border-[#E01E26]/25" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <Skull className="h-14 w-14 text-[#E01E26]" strokeWidth={1.25} />
        <span className="font-serif text-[10px] uppercase tracking-[0.4em] text-[#C7CACE]">
          Fork · Fate
        </span>
      </div>
    </div>
  );
}

// Tarot-style front: photo centered inside a card with matching themed frame
function CardFront({ src, light, theme }) {
  const gilded = theme === "fantasy" || theme === "fairy"; // gold-framed realms
  return (
    <div className={`absolute inset-0 ${light ? "bg-[#F5F0E6]" : "bg-[#0E0E0E]"}`} data-testid="card-front">
      <div
        className="absolute inset-0"
        style={{ background: gilded
          ? "radial-gradient(circle at 50% 42%, rgba(230,178,58,0.16), rgba(0,0,0,0) 62%)"
          : light
          ? "radial-gradient(circle at 50% 42%, rgba(163,22,33,0.10), rgba(255,255,255,0) 62%)"
          : "radial-gradient(circle at 50% 42%, rgba(224,30,38,0.20), rgba(0,0,0,0) 62%)" }}
      />
      <div className="absolute inset-[13px] overflow-hidden rounded-md">
        <img src={src} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
      </div>
      <div className={`absolute inset-2 rounded-xl border ${light ? "border-[#A31621]/60" : "border-[#E01E26]/70"}`} style={gilded ? { borderColor: "rgba(230,178,58,0.7)" } : undefined} />
      <div className={`absolute inset-[10px] rounded-lg border ${light ? "border-[#A31621]/25" : "border-[#E01E26]/25"}`} style={gilded ? { borderColor: "rgba(230,178,58,0.28)" } : undefined} />
    </div>
  );
}

export function ShufflingDeck({ cards, flash, landed, light, theme, season, seasonItems, seasonAccent }) {
  const source = cards.length ? cards : (flash ? [flash] : []);
  // 30-day streak reward: the whole deck turns golden for loyal dealers.
  const golden = readStreak() >= 30;
  // Always fill a full visual deck so the shuffle never looks like a single card,
  // even when the filtered result set is tiny (repeats are visual-only).
  const base = [];
  for (let i = 0; source.length && base.length < DECK_SIZE; i++) {
    base.push(source[i % source.length]);
  }
  const deck = (landed && flash
    ? [flash, ...base.filter((c) => c?.id !== flash?.id)]
    : base
  ).slice(0, DECK_SIZE);
  const label = flash?.name;
  // Rotates the stacking order once per riffle so cards genuinely pass over and under
  // each other and come back in a different order — the part that makes it read as a
  // shuffle instead of a wobble. Integer z-index only (never interpolated, which would
  // produce fractional values the browser discards).
  const [cycle, setCycle] = React.useState(0);
  React.useEffect(() => {
    if (landed) return;
    const id = setInterval(() => setCycle((c) => c + 1), RIFFLE_MS);
    return () => clearInterval(id);
  }, [landed]);
  React.useEffect(() => { if (landed) setCycle(0); }, [landed]);
  return (
    <div className="grid h-full min-h-[400px] place-items-center" data-testid="shuffling-deck">
      <div className="flex flex-col items-center gap-8">
        <div className="relative h-72 w-44">
          <AnimatePresence>
            {landed && theme === "dark" && (
              <motion.div
                className="pointer-events-none absolute left-1/2 top-1/2 z-50"
                style={{ transform: "translate(-50%, calc(-50% + 48px))" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="skeleton-hands-overlay"
              >
                <motion.img
                  src="/skeleton-hand.png"
                  alt=""
                  className="w-[310px] max-w-none select-none drop-shadow-2xl"
                  initial={{ scale: 1.15 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 240, damping: 18 }}
                />
              </motion.div>
            )}
            {landed && theme === "fantasy" && [
              // Two copies of the same PNG with complementary clip-paths so the
              // dragon actually *holds* the card: the palm slice renders BEHIND
              // the card (z-1 < card z-5) with its arc peeking out below the
              // bottom edge, while the talon/thumb/fingers slice renders in
              // front (z-50) wrapping over the card face. Seams are placed on
              // near-transparent alpha rows (png y≈868, x≈216) so no hard cut
              // is visible. Geometry computed from alpha measurements — see
              // /app/scripts/measure_claw_window.py.
              { key: "claw-behind", z: "z-[1]", clip: "polygon(24.1% 72.3%, 100% 72.3%, 100% 100%, 24.1% 100%)" },
              { key: "claw-front", z: "z-50", clip: "polygon(0% 0%, 100% 0%, 100% 72.3%, 24.1% 72.3%, 24.1% 100%, 0% 100%)" },
            ].map((layer) => (
              <motion.div
                key={layer.key}
                className={`pointer-events-none absolute left-1/2 top-1/2 ${layer.z}`}
                // Maps the PNG's grip window (png x 210-699, talon tip y 268,
                // palm top y 865) onto the 176x288 card: talon hooks ~28px over
                // the top edge, fingers wrap the right side, thumb the left.
                style={{ transform: "translate(calc(-50% - 2px), calc(-50% + 18px))" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                // Fast exit — the reveal card transitions in right after
                // the deck lands, and the claw is anchored to the smaller
                // deck size. If it lingers into the reveal, it looks like
                // the dragon is clutching only the photo area rather than
                // the whole reveal card. Snap it away before the reveal
                // begins so users only see the claw over the deck card.
                transition={{ duration: 0.12 }}
                data-testid={layer.key === "claw-front" ? "dragon-claw-overlay" : "dragon-claw-overlay-behind"}
              >
                <motion.img
                  src="/dragon-claw.png"
                  alt=""
                  className="w-[323px] max-w-none select-none"
                  style={{
                    filter: "drop-shadow(0 10px 16px rgba(0,0,0,0.55))",
                    clipPath: layer.clip,
                    WebkitClipPath: layer.clip,
                    // Mild vertical stretch so the grip window height matches
                    // the card. NOTE: must be framer's scaleY prop, not a raw
                    // transform string — motion.img overwrites `transform`
                    // when animating scale, which silently dropped earlier
                    // scaleX tweaks.
                    scaleY: 1.12,
                  }}
                  initial={{ scale: 1.2, rotate: -3 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 240, damping: 18 }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          {landed && <ThemeFlourish theme={theme} variant="deck" />}
          {landed && theme === "cyber" && (
            <motion.div
              className="pointer-events-none absolute inset-[-14px] z-40 rounded-3xl"
              data-testid="cyber-neon-pulse"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0.15, 1, 0.15, 1, 0] }}
              transition={{ duration: 1.2, times: [0, 0.12, 0.28, 0.45, 0.62, 0.8, 1], ease: "easeOut" }}
              style={{ border: "2px solid rgba(34,224,224,0.95)", boxShadow: "0 0 24px 6px rgba(34,224,224,0.9), 0 0 60px 18px rgba(199,125,255,0.7), inset 0 0 22px rgba(34,224,224,0.5)" }}
            />
          )}
          {landed && theme === "fantasy" && (
            <motion.div
              className="pointer-events-none absolute inset-[-14px] z-40 rounded-3xl"
              data-testid="fantasy-gold-pulse"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0.2, 1, 0.2, 0.9, 0] }}
              transition={{ duration: 1.3, times: [0, 0.14, 0.3, 0.46, 0.62, 0.8, 1], ease: "easeOut" }}
              style={{ border: "2px solid rgba(230,178,58,0.95)", boxShadow: "0 0 26px 7px rgba(230,178,58,0.9), 0 0 64px 20px rgba(224,86,30,0.6), inset 0 0 22px rgba(230,178,58,0.5)" }}
            />
          )}
          {deck.map((c, i) => {
            // Once landed, render only the winning card — no backing cards to peek out as lines
            if (landed && i !== 0) return null;
            const showPhoto = landed && i === 0 && cardImage(c);
            return (
            <motion.div
              key={(c?.id || "c") + i}
              className={`absolute inset-0 overflow-hidden rounded-2xl border-2 shadow-2xl ${season ? "bg-[#F5F0E6] shadow-black/10" : light ? "border-[#D9C9A8] bg-[#F5F0E6] shadow-black/10" : "border-[#E01E26] bg-[#0E0E0E] shadow-black/30"}`}
              style={{
                ...(theme === "fairy" ? { borderColor: "#E6B23A" } : null),
                zIndex: landed ? DECK_SIZE - i : DECK_SIZE - ((i + cycle) % DECK_SIZE),
                // Pivot on the corner facing the centre of the deck so each packet fans
                // open like real cards instead of sliding like a flat tile.
                transformOrigin: landed ? "50% 50%" : (i % 2 === 0 ? "100% 100%" : "0% 100%"),
                ...(season && seasonAccent ? { borderColor: seasonAccent } : {}),
                ...(theme === "fantasy" ? { borderColor: "#E6B23A" } : {}),
              }}
              animate={
                landed
                  ? {
                      x: 0,
                      y: 0,
                      rotate: 0,
                      scale: i === 0 ? 1.05 : 0.96,
                      opacity: i === 0 ? 1 : 0,
                    }
                  : riffleKeyframes(i)
              }
              transition={
                landed
                  ? { type: "spring", stiffness: 320, damping: 22 }
                  : {
                      duration: RIFFLE_MS / 1000,
                      times: RIFFLE_TIMES,
                      repeat: Infinity,
                      ease: RIFFLE_EASE,
                      // Staggered so the packets interleave card-by-card rather than
                      // slamming together as two solid blocks.
                      delay: i * 0.055,
                    }
              }
            >
              {showPhoto ? (
                <CardFront src={cardImage(c)} light={light} theme={theme} />
              ) : (
                <CardBack light={light} theme={theme} golden={golden} seasonItem={season && seasonItems ? seasonItems[i % seasonItems.length] : null} />
              )}
            </motion.div>
            );
          })}
        </div>
        <div className="relative z-[60] text-center">
          <p className={`font-sans text-xs font-bold uppercase tracking-[0.25em] ${light ? "text-[#A31621]" : "text-[#E01E26]"}`} style={season && seasonAccent ? { color: seasonAccent } : (theme === "fantasy" || theme === "fairy" ? { color: "#E6B23A" } : undefined)}>
            {landed ? (light ? "Your pick" : "Fate has chosen") : (light ? "Shuffling…" : "Shuffling the deck")}
          </p>
          <p className={`mt-1 h-7 font-serif text-2xl drop-shadow ${light ? "text-[#18181B]" : "text-white"}`}>{label}</p>
        </div>
      </div>
    </div>
  );
}
